/**
 * Componente de métricas de logística
 * Muestra los porcentajes de envío, entrega, tránsito y devolución
 * Logistics metrics component - Displays shipping, delivery, transit and return percentages
 */

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckCircle, faTruck, faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { GlobalMetrics } from '../../models/LogisticsApiResponse';

interface LogisticsMetricsProps {
  globalMetrics?: GlobalMetrics;
  isLoading?: boolean;
}

export const LogisticsMetrics = ({ globalMetrics }: LogisticsMetricsProps) => {
  const { t } = useTranslation('logisticsModule');

  // Métricas de logística con sus iconos y colores correspondientes
  // Logistics metrics with their corresponding icons and colors
  const metrics = [
    {
      title: t('metrics.sent.title'),
      value: globalMetrics ? `${globalMetrics.send_rate.toFixed(2)}%` : '0.00%',
      icon: faCheck,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-info', // Azul claro para porcentaje enviado
      dataTestId: 'logistics-metric-sent',
    },
    {
      title: t('metrics.delivered.title'),
      value: globalMetrics ? `${globalMetrics.delivery_rate.toFixed(2)}%` : '0.00%',
      icon: faCheckCircle,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-success', // Verde para porcentaje entregado
      dataTestId: 'logistics-metric-delivered',
    },
    {
      title: t('metrics.in_transit.title'),
      value: globalMetrics ? `${globalMetrics.transit_rate.toFixed(2)}%` : '0.00%',
      icon: faTruck,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-warning', // Naranja para porcentaje en tránsito
      dataTestId: 'logistics-metric-in-transit',
    },
    {
      title: t('metrics.returned.title'),
      value: globalMetrics ? `${globalMetrics.return_rate.toFixed(2)}%` : '0.00%',
      icon: faArrowLeft,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-error', // Rosa/salmón para porcentaje devuelto
      dataTestId: 'logistics-metric-returned',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric, index) => (
        <SectionBox key={index} data-testid={metric.dataTestId}>
          {/* Contenedor principal de la métrica - Main metric container */}
          <div className="flex items-center gap-3">
            {/* Icono de la métrica - Metric icon */}
            <div className={`w-8 h-8 rounded-md ${metric.iconBgClass} flex items-center justify-center flex-shrink-0`}>
              <FontAwesomeIcon icon={metric.icon} className={`w-3.5 h-3.5 ${metric.iconTextClass}`} />
            </div>
            {/* Información de la métrica - Metric information */}
            <div className="flex-1">
              {/* Título de la métrica - Metric title */}
              <h3 className="text-sm font-medium text-complementary-text mb-1">{metric.title}</h3>
              {/* Valor de la métrica - Metric value */}
              <p className="text-base font-bold text-title">{metric.value}</p>
            </div>
          </div>
        </SectionBox>
      ))}
    </div>
  );
};
